/**
 * Facilitate compound tool — session resume and constellation commit.
 *
 * Actions: resume, commit-constellation
 *
 * Scoring, coaching, and facilitation loop moved to agent-side skills (BET-106).
 * This tool retains only session-state loading and atomic constellation commit.
 * WP-321 E3: Delegates to kernel actions (agentKnowledge.facilitateEnvelope) —
 * MCP layer is a thin passthrough with presentation-only enrichment (cortexUrl).
 *
 * Chain: ENT-37 (Facilitate v1), DEC-56 (PB judges, agents coach),
 *        STA-1 (Constellation Capture Pattern)
 * Rules: BR-8 (session required), BR-22 (single impl), BR-25 (MCP layer),
 *        BR-70 (audit trail)
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { z } from "zod/v3";
import {
  kernelCallEnvelope,
  getWorkspaceContext,
  requireWriteAccess,
  recordSessionActivity,
  getAgentSessionId,
} from "../client.js";
import { runConflictPreflight } from "./conflict-preflight.js";
import { failure, validationResult } from "../envelope.js";

// ── Schema ─────────────────────────────────────────────────────────────────

const FACILITATE_ACTIONS = ["resume", "commit-constellation"] as const;

// WP-465 slice ⑤ (TEN-2439, §3.3): mirrors the kernel's coherencyAcknowledgementValidator
// (convex/lib/gates/coherencyRecord.ts) — response is a free string here; the gate owns the
// vocabulary check (validateCoherencyAcknowledgement), fail-closed, so the connector mirror
// can never drift the SSOT.
const coherencyAcknowledgementSchema = z.object({
  response: z.string().max(200)
    .describe("Acknowledgement response per offender: 'linked' | 'accepted-fix' | 'diverged'."),
  entryId: z.string().max(200).optional()
    .describe("Entry the acknowledgement links to (e.g. the strategic spine entry)."),
  reason: z.string().max(2000).optional()
    .describe("Free-text justification, required for a 'diverged' response."),
  subjectEntryId: z.string().max(200).optional()
    .describe("The refused entry this acknowledgement answers."),
});

export const facilitateSchema = z.object({
  action: z.enum(FACILITATE_ACTIONS).describe(
    "'resume': load session state from an existing bet entry. " +
    "'commit-constellation': atomically accept a bet and all its linked draft entries in one call. Requires betEntryId.",
  ),
  betEntryId: z.string().max(200).optional()
    .describe("Bet entry ID. Required for both actions."),
  operationId: z.string().max(200).optional()
    .describe("Optional idempotency key for commit-constellation retries."),
  // WP-465 slice ⑤: coherency retry controls for a COHERENCY_REFUSED constellation hold.
  // Forwarded verbatim to agentKnowledge.facilitateEnvelope (validation lives at the gate).
  coherencyAcknowledgements: z.array(coherencyAcknowledgementSchema).max(20).optional()
    .describe(
      "Per-offender acknowledgements to retry a constellation held under standard/strict coherency mode. " +
      "Each: {subjectEntryId, response: 'linked' | 'accepted-fix' | 'diverged', entryId?, reason?}.",
    ),
  steeringOverrideReason: z.string().max(2000).optional()
    .describe(
      "Typed override reason (>= 12 chars) to push a constellation past a coherency hold instead of acknowledging.",
    ),
});

// ── Types ──────────────────────────────────────────────────────────────────

interface SessionDraft {
  entryId: string;
  name: string;
  collection: string;
  relationType: string;
  status: string;
}

interface ConstellationState {
  elementCount: number;
  riskCount: number;
  decisionCount: number;
}

interface BetEntryData {
  betEntryId: string;
  name: string;
  status: string;
  data: Record<string, unknown>;
}

interface ResumeData {
  betEntry: BetEntryData;
  constellation: ConstellationState;
  sessionDrafts: SessionDraft[];
}

interface ConstellationCommitResult {
  operationId: string;
  committedIds: string[];
  alreadyCommittedIds: string[];
  proposedIds: string[];
  failedIds: Array<{ entryId: string; name: string; error: string }>;
  conflictIds: Array<{ entryId: string; name: string; reason: string }>;
  totalRelations: number;
  proposalCreated?: boolean;
}

interface CommitConstellationData {
  result: ConstellationCommitResult;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function buildCortexUrl(workspaceSlug: string, entryId: string): string {
  const appUrl = process.env.PRODUCTBRAIN_APP_URL ?? "https://work.productbrain.io";
  return `${appUrl}/${workspaceSlug}/legacy/entries/${entryId}`;
}

// WP-484 S3 (clean break, TEN-2157): the standalone `facilitate` tool
// retired here. handleResume / handleCommitConstellation stay live —
// session.ts (the `session` compound, action=resume / action=commit-constellation)
// is their only remaining caller.

// ── Action Handlers ────────────────────────────────────────────────────────

export async function handleResume(args: z.infer<typeof facilitateSchema>) {
  const betId = args.betEntryId;
  if (!betId) {
    return validationResult("`betEntryId` is required for resume action.");
  }

  const { workspaceId, workspaceSlug } = await getWorkspaceContext();

  // `agentKnowledge.facilitateEnvelope` is self-enveloping (same shape as `agentKnowledge.
  // wrapupEnvelope`, see packages/mcp-server/src/tools/wrapup.ts's own doc comment) — its
  // declared return is `Envelope<ResumeData | CommitConstellationData>`, and the payload
  // this "resume" call receives is specifically `ResumeData` (has `betEntry`, unique to it;
  // `CommitConstellationData` has `result` instead). PR #395, Lane C S3b annotation pass.
  type FacilitateEnvelopePayload = Extract<
    GatewayRouteReturnByName<"agentKnowledge.facilitateEnvelope">,
    { ok: true }
  >["data"];
  type ResumeEnvelopeData = Extract<FacilitateEnvelopePayload, { betEntry: unknown }>;

  // Delegate to kernel action
  const envelope = await kernelCallEnvelope<ResumeEnvelopeData>("agentKnowledge.facilitateEnvelope", {
    workspaceId,
    action: "resume",
    betEntryId: betId,
  });

  // MCP-layer enrichment: add cortexUrl (kernel doesn't build Cortex URLs — that's MCP presentation)
  const enrichedData = {
    ...envelope.data,
    cortexUrl: buildCortexUrl(workspaceSlug, betId),
  };

  // Rebuild rich markdown briefing (presentation concern — kernel stays semantic)
  const bet = enrichedData.betEntry;
  const cx = enrichedData.constellation;
  const constellationParts: string[] = [];
  if (cx.elementCount > 0) constellationParts.push(`${cx.elementCount} element(s)`);
  if (cx.riskCount > 0) constellationParts.push(`${cx.riskCount} risk(s)`);
  if (cx.decisionCount > 0) constellationParts.push(`${cx.decisionCount} decision(s)`);
  const constellationSummary = constellationParts.length > 0
    ? constellationParts.join(", ")
    : "no entries captured yet";

  const draftLines = enrichedData.sessionDrafts.slice(0, 10).map(
    (d: SessionDraft) => `- \`${d.entryId}\` ${d.name} [${d.collection}] (${d.status})`,
  );
  const textLines = [
    `# ${bet.name}`,
    "",
    `**ID:** \`${bet.betEntryId}\` · **Status:** ${bet.status}`,
    enrichedData.cortexUrl ? `**Cortex:** ${enrichedData.cortexUrl}` : "",
    "",
    `**Constellation:** ${constellationSummary}`,
    ...(draftLines.length > 0 ? ["", "**Session drafts:**", ...draftLines] : []),
  ].filter((l) => l !== undefined);

  return {
    content: [{ type: "text" as const, text: textLines.join("\n") }],
    structuredContent: {
      ok: true as const,
      summary: envelope.summary,
      data: enrichedData,
      next: envelope.next,
      _meta: envelope._meta,
    },
  };
}

// ── Commit Constellation ────────────────────────────────────────────────

export async function handleCommitConstellation(args: z.infer<typeof facilitateSchema>) {
  requireWriteAccess();

  const betId = args.betEntryId;
  if (!betId) {
    return validationResult("`betEntryId` is required for commit-constellation action.");
  }

  const { workspaceId } = await getWorkspaceContext();
  const sessionId = getAgentSessionId();
  const operationId = args.operationId ?? `${betId}:${sessionId ?? "sessionless"}:${Date.now()}`;

  // ── MCP-layer conflict preflight (pre-condition check, stays in MCP layer) ──
  // We need the bet name and description for the conflict check. Load minimally.
  let betName = betId;
  let betDesc = "";
  let betCollectionHint: string | undefined;
  try {
    type BetEntryShape = {
      name: string;
      data?: Record<string, unknown>;
      collectionSlug?: string;
      collection?: { slug?: string; name?: string };
    };
    const betEnvelope = await kernelCallEnvelope<BetEntryShape>("chain.getEntry", { entryId: betId });
    const entry = betEnvelope.data;
    if (entry) {
      betName = entry.name ?? betId;
      const d = entry.data ?? {};
      const links = d["links"] as Record<string, unknown> | undefined;
      betDesc = (d.problem as string) ?? (links?.problem as string) ?? (d.description as string) ?? "";
      betCollectionHint = entry.collectionSlug ?? entry.collection?.slug ?? entry.collection?.name;
    }
  } catch {
    // If we can't load the bet entry, the kernel will handle the NOT_FOUND case
  }

  // WP-485 Slice 2b (R2, FEAT-1370): discovery only — the local
  // `blockingContradictions.length > 0` hard-block branch (a client-side
  // BLOCKING_CONTRADICTION_THRESHOLD comparison, one of three independent copies of the same
  // enforcement decision) is deleted. This tool discovers conflicts and keeps them visible as
  // advisory text below; it never decides to block a constellation accept.
  const preflight = await runConflictPreflight(betName, betDesc, betCollectionHint);

  if (preflight.source === "semantic" ? preflight.conflicts.length > 0 : preflight.advisoryWarnings.length > 0) {
    await recordSessionActivity({ contradictionWarning: true });
  }

  // WP-485 Slice 2b round 2 (Codex P1, FEAT-1370): forward discovered semantic conflicts as
  // evidence to batchCommitConstellation's PRE-EXISTING contradiction gate (TEN-1405 / BR-144)
  // via facilitateEnvelope's new `conflicts` arg — without this, that gate always evaluated an
  // empty set on this surface no matter what was discovered. Heuristic-source
  // `advisoryWarnings` are a different shape and never forwarded. Whitelist only the
  // validator-accepted fields — `chain.detectSemanticConflicts` also returns an internal
  // `chainEntryDocId` that fails the server's `conflicts` arg validator (Slice 2b round 1
  // live-proof finding).
  const conflictEvidence = preflight.source === "semantic"
    ? preflight.conflicts.map((c) => ({
        chainEntryId: c.chainEntryId,
        chainEntryName: c.chainEntryName,
        ...(c.chainEntryDescription ? { chainEntryDescription: c.chainEntryDescription } : {}),
        type: c.type,
        confidence: c.confidence,
        explanation: c.explanation,
      }))
    : [];

  // ── Delegate to kernel action ───────────────────────────────────────────
  let envelope: Awaited<ReturnType<typeof kernelCallEnvelope<CommitConstellationData>>>;
  try {
    // Matches the pre-declared `envelope` variable's own type above — see the "resume" call
    // site's doc comment for why this route's payload must be narrowed, not used as-is.
    envelope = await kernelCallEnvelope<CommitConstellationData>("agentKnowledge.facilitateEnvelope", {
      workspaceId,
      action: "commit-constellation",
      betEntryId: betId,
      operationId,
      sessionId: sessionId ?? undefined,
      author: sessionId ? `agent:${sessionId}` : "kernel:facilitate",
      // WP-465 slice ⑤ (TEN-2439, §3.7): relay the coherency retry controls so a
      // COHERENCY_REFUSED constellation hold is actually retryable through MCP.
      ...(args.coherencyAcknowledgements ? { coherencyAcknowledgements: args.coherencyAcknowledgements } : {}),
      ...(args.steeringOverrideReason ? { steeringOverrideReason: args.steeringOverrideReason } : {}),
      ...(conflictEvidence.length > 0 ? { conflicts: conflictEvidence } : {}),
    });
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    const code = (err as { code?: string })?.code;
    const diagnostics = (err as { diagnostics?: Record<string, unknown> })?.diagnostics;

    // WP-465 slice ⑤ (TEN-2439, §3.7): a coherency HOLD is NOT a backend error. The
    // kernel returns an `ok:false` COHERENCY_REFUSED envelope naming each offender and
    // its routes; callGateway throws on `ok:false`, so detect the code here — BEFORE the
    // generic wrapper — and surface the structured `coherencyRefusals` (subject ids +
    // routes) so the agent can populate the retry controls (coherencyAcknowledgements /
    // steeringOverrideReason). Mirrors the kernel facilitate envelopeError shape and the
    // commit/update single-entry refusal tools — same code, same diagnostics key.
    if (code === "COHERENCY_REFUSED") {
      const coherencyRefusals = (Array.isArray(diagnostics?.coherencyRefusals)
        ? diagnostics.coherencyRefusals
        : []) as Array<{ entryId: string; name: string; refusal: Record<string, unknown> }>;
      const offenderLines = coherencyRefusals
        .map((r) => `- \`${r.entryId}\` ${r.name}: ${String((r.refusal as { reason?: string })?.reason ?? "coherency challenge unsatisfied")}`)
        .join("\n");
      const count = coherencyRefusals.length;
      return {
        content: [{
          type: "text" as const,
          text: [
            `# Constellation held by the coherency gate`,
            "",
            `\`${betId}\` was not accepted — ${count} entr${count === 1 ? "y" : "ies"} refused. Nothing was committed; drafts persist.`,
            ...(offenderLines ? ["", offenderLines] : []),
          ].join("\n"),
        }],
        structuredContent: failure(
          "COHERENCY_REFUSED",
          `Constellation held: ${count} entr${count === 1 ? "y" : "ies"} refused by the coherency gate — nothing was committed, drafts persist.`,
          "Respond per offender and retry commit-constellation: link the entry to the strategic spine, pass coherencyAcknowledgements [{subjectEntryId, response: 'linked' | 'accepted-fix' | 'diverged', entryId?, reason?}], or supply a typed steeringOverrideReason (at least 12 characters).",
          [{ tool: "session", description: "Retry with coherency acknowledgements", parameters: { action: "commit-constellation", betEntryId: betId } }],
          { coherencyRefusals },
        ),
      };
    }

    // WP-485 Slice 2b round 4 (Codex P2, FEAT-1370): DEAD-BUT-TYPED. `{graduated:false}`
    // (DEC-1321) means this never fires today, but a future policy graduation would otherwise
    // reach here as an unclassified BACKEND_ERROR — the exact code-loss the round-2 AKI gateway
    // registration fix exists to prevent, for this DIFFERENT (facilitateEnvelope, not AKI HTTP)
    // transport path. Preserve the code + message the kernel now returns.
    if (code === "CONTRADICTION_BLOCKED") {
      return {
        content: [{
          type: "text" as const,
          text: `# Constellation accept blocked\n\n\`${betId}\` was not accepted: ${msg}\n\nNo constellation entries were accepted.`,
        }],
        structuredContent: failure(
          "CONTRADICTION_BLOCKED",
          msg,
          "Resolve the contradiction before retrying commit-constellation.",
        ),
      };
    }

    return {
      content: [{
        type: "text" as const,
        text: `# Work package accept failed\n\n\`${betId}\` could not be accepted: ${msg}\n\nNo constellation entries were accepted.`,
      }],
      structuredContent: failure(
        "BACKEND_ERROR",
        `Work package accept failed: ${msg}`,
        "Retry or check for accept blockers.",
        [{ tool: "session", description: "Resume session", parameters: { action: "resume", betEntryId: betId } }],
        diagnostics,
      ),
    };
  }

  // WP-485 Slice 2b round 2 (Codex P2, FEAT-1370): the discovery-time call above (line ~231)
  // already records `contradictionWarning` once per preflight — this second, post-commit call
  // used to record the SAME boolean again, double-counting every single discovered conflict as
  // two in session summaries and related instrumentation. Deleted, not moved: discovery-time is
  // the right moment (matches knowledge.ts's single recordSessionActivity call at discovery).
  // Per-entry audit trail — record each committed doc in session activity (BR-70)
  for (const docId of (envelope.data?.result?.committedIds ?? [])) {
    try {
      await recordSessionActivity({ entryModified: docId });
    } catch {
      // Advisory — non-blocking
    }
  }

  // Build text output from kernel result
  const result = envelope.data.result;
  // WP-485 Slice 2b round 3 (Codex P1, FEAT-1370): mirror the SAME discovered conflicts the
  // text below renders into the structured payload — before this fix, `suggestions` carried
  // only `conflictPreflightSource`, so a structured-content-only caller saw an unqualified
  // successful accept with no trace of a discovered contradiction. (batchCommitConstellation
  // does not attach a server-side `contradictionAdvisory` on success — unlike commitEntry,
  // Slice 2a never wired that for this surface — so this mirrors the discovery-level
  // evidence, the same data already forwarded to the gate as `conflicts`.)
  const suggestions = {
    conflictPreflightSource: preflight.source,
    ...(preflight.source === "semantic" && preflight.conflicts.length > 0
      ? { conflicts: preflight.conflicts }
      : {}),
    ...(preflight.source === "heuristic" && preflight.advisoryWarnings.length > 0
      ? { advisoryWarnings: preflight.advisoryWarnings }
      : {}),
  };

  const lines: string[] = [];
  const hasIssues = (result.failedIds?.length ?? 0) > 0 || (result.conflictIds?.length ?? 0) > 0;
  if (result.proposalCreated) {
    lines.push(
      hasIssues ? `# Proposal created with issues` : `# Proposal created`,
      "",
      `Workspace uses consent-based governance. \`${betId}\` was submitted as a proposal — it will be accepted when approved.`,
      `No linked entries were accepted because the work package itself is still awaiting approval.`,
    );
  } else {
    lines.push(
      hasIssues ? `# Accepted with issues` : `# Accepted`,
      "",
      `**${result.committedIds?.length ?? 0} entries** accepted onto the Chain, **${result.totalRelations} connections** preserved.`,
      hasIssues
        ? `\`${betId}\` was partially accepted. Review conflicts/failed entries below before considering the constellation complete.`
        : `\`${betId}\` and its full constellation are now source of truth.`,
    );
  }

  if (preflight.source === "semantic" && preflight.conflicts.length > 0) {
    lines.push(
      "",
      `⚠ **Semantic conflict preflight** (advisory):`,
      ...preflight.conflicts.map((c) => `- \`${c.chainEntryId}\` ${c.chainEntryName}: ${c.explanation}`),
    );
  } else if (preflight.source === "heuristic" && preflight.advisoryWarnings.length > 0) {
    lines.push(
      "",
      `⚠ **Contradiction warnings** (advisory):`,
      ...preflight.advisoryWarnings.map(
        (w) => `- \`${w.entryId}\` ${w.name} [${w.collection}] (${w.governsCount} governs relation${w.governsCount === 1 ? "" : "s"})`,
      ),
    );
  }

  if ((result.committedIds?.length ?? 0) > 0) {
    lines.push("", `**Accepted:** ${result.committedIds.join(", ")}`);
  }
  if ((result.alreadyCommittedIds?.length ?? 0) > 0) {
    lines.push("", `**Already accepted:** ${result.alreadyCommittedIds.join(", ")}`);
  }
  if ((result.proposedIds?.length ?? 0) > 0) {
    lines.push("", `**Proposed (not yet accepted):** ${result.proposedIds.join(", ")}`);
  }
  if ((result.conflictIds?.length ?? 0) > 0) {
    lines.push("", `**${result.conflictIds.length} conflicts:**`);
    for (const c of result.conflictIds) {
      lines.push(`- \`${c.entryId}\` ${c.name}: ${c.reason}`);
    }
  }
  if ((result.failedIds?.length ?? 0) > 0) {
    lines.push("", `**${result.failedIds.length} failed:**`);
    for (const f of result.failedIds) {
      lines.push(`- \`${f.entryId}\` ${f.name}: ${f.error}`);
    }
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: {
      ok: true as const,
      summary: envelope.summary,
      data: { result, suggestions },
      next: envelope.next,
      _meta: envelope._meta,
    },
  };
}
